import React, { useEffect, useRef, useState } from "react";
import { Input, Drawer, Modal } from "antd";
import { message } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import Keyboard from "react-simple-keyboard";
import Close from "../../../../../assets/images/close-x.svg";
import { DRAWER_MASK, initialsOf } from "../../../Core/modals/customerDrawerUi";
import "../../modals/v4Modals.css";
import db from "../../../../../database";
import useCartStore from "../../../../../stores/useCartStore";
import useTransactionStore from "../../../../../stores/useTransactionStore";

const SalesRepDrawer = ({
  openSalesRep,
  setOpenSalesRep,
  cart,
  setCart,
  posConfig,
  keyboardType,
  setKeyboardType,
  salesRepValue,
  setSalesRepValue,
  selectedProductInCart,
  searchInputRef,
}) => {
  const [salesRepData, setSalesRepData] = useState([]);
  const [selectedSalesRep, setSelectedSalesRep] = useState({});
  const [open, setOpen] = useState(false);
  const [layoutName, setLayoutName] = useState("default");
  const salesRepRef = useRef(null);
  const keyboardRef = useRef(null);

  const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";

  const forLine = openSalesRep?.level === "line";
  const billCart = isExchange ? useCartStore.getState().exchangeCart || {} : cart;
  const currentRepId = (forLine ? selectedProductInCart?.salesRepId : billCart?.salesRepId) || null;
  const canClear = posConfig?.requireSalesRepresentative !== "Y";

  const fetchSalesRepData = async (searchTerm) => {
    const allReps = await db.salesRep.toArray();
    if (!searchTerm) return allReps;
    const term = searchTerm.toLowerCase();
    return allReps.filter((res) => res.name?.toLowerCase().includes(term) || res.code?.toLowerCase().includes(term));
  };

  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setSalesRepValue(value);
    const data = await fetchSalesRepData(value);
    setSalesRepData(data);
    if (!value) {
      setSalesRepData(data);
      return;
    }
  };

  useEffect(() => {
    const run = async () => {
      const res = await db.salesRep.toArray();
      setSalesRepData(res);
    };
    run();
  }, []);

  useEffect(() => {
    if (openSalesRep?.flag && salesRepRef.current) {
      setTimeout(() => {
        salesRepRef.current?.focus();
      }, 10);
    }
  }, [openSalesRep?.flag]);

  useEffect(() => {
    const fetchSalesReps = async () => {
      if (openSalesRep?.flag) {
        try {
          const data = await fetchSalesRepData("");
          setSalesRepData(data);
          setSalesRepValue("");
        } catch (error) {
          console.error("Error fetching sales representatives:", error);
        }
      }
    };
    fetchSalesReps();
  }, [openSalesRep?.flag]);

  const handleKeyboardInput = (key) => {
    if (key === "{shift}") {
      setLayoutName(layoutName === "shift" ? "default" : "shift");
      return;
    }
    if (key === "{number}") { setLayoutName("number"); return; }
    if (key === "{numbers}") { setLayoutName("numbers"); return; }
    if (key === "{small}") { setLayoutName("shift"); return; }
    if (key === "{caps}") { setLayoutName("default"); return; }
  };

  const handleClose = () => {
    if (posConfig?.requireSalesRepresentative === "Y") {
      const exchangeCartData = useCartStore.getState().exchangeCart;
      if (exchangeCartData) {
        if (exchangeCartData?.salesRepId === null) {
          setOpenSalesRep({ flag: true, level: "order" });
        }
      } else {
        if (cart?.salesRepId === null) {
          setOpenSalesRep({ flag: true, level: "order" });
        }
      }
      message.warning("Please select a Sales Representative to continue with this bill.");
    } else {
      setSelectedSalesRep({});
      setOpenSalesRep({ flag: false, level: "" });
      setOpen(false);
      setLayoutName("default");
      setSalesRepValue("");
      if (searchInputRef?.current) {
        searchInputRef?.current.focus();
      }
    }
  };

  const assignSalesRep = async (rep) => {
    let repId = rep.s_salesrep_id;
    let repName = rep.name;
    if (repId && !repName) {
      const dbRep = await db.salesRep.where("s_salesrep_id").equals(repId).first().catch(() => null);
      repName = dbRep?.name || "";
    }
    let cartData = isExchange ? (useCartStore.getState().exchangeCart || {}) : { ...cart };
    if (openSalesRep.level === "order") {
      cartData.salesRepId = repId;
      cartData.salesRepName = repName;
      if (isExchange) {
        useCartStore.getState().setExchangeCart({ ...cartData });
        const currentCart = useCartStore.getState().cart;
        setCart({ ...currentCart, salesRepId: repId, salesRepName: repName });
      } else {
        setCart({ ...cartData });
      }
      setSelectedSalesRep({});
      setOpenSalesRep({ flag: false, level: "" });
    } else if (openSalesRep.level === "line") {
      const updatedItems = cartData?.items?.map((lineItem) => {
        if (lineItem.productId === selectedProductInCart.productId && lineItem.lineId === selectedProductInCart.lineId) {
          return { ...lineItem, salesRepId: repId, salesRepName: repName };
        }
        return lineItem;
      });
      cartData = { ...cartData, items: updatedItems };
      if (isExchange) {
        useCartStore.getState().setExchangeCart(cartData);
        const currentCart = useCartStore.getState().cart;
        const mainItems = currentCart?.items?.map((lineItem) => {
          if (lineItem.productId === selectedProductInCart.productId && lineItem.lineId === selectedProductInCart.lineId) {
            return { ...lineItem, salesRepId: repId, salesRepName: repName };
          }
          return lineItem;
        });
        setCart({ ...currentCart, items: mainItems });
      } else {
        setCart(cartData);
      }
      setSelectedSalesRep({});
      setOpenSalesRep({ flag: false, level: "" });
    }
  };

  return (
    <>
      <Modal
        open={openSalesRep?.flag}
        closable={false}
        footer={null}
        keyboard={false}
        width={480}
        className="v4m"
        styles={{ mask: DRAWER_MASK }}
        onCancel={handleClose}
      >
        <div className="v4m-head">
          <div>
            <h2 className="v4m-title">{forLine ? "Sales rep for this line" : "Sales rep for this bill"}</h2>
            <p className="v4m-sub">
              {forLine && selectedProductInCart?.name ? selectedProductInCart.name : "Applies to every item on the bill that has no rep of its own"}
            </p>
          </div>
          <button type="button" className="v4m-ib" aria-label="Close" onClick={handleClose}>
            <CloseOutlined />
          </button>
        </div>
        <Input
          type="text"
          ref={salesRepRef}
          value={salesRepValue}
          className="v4m-in"
          placeholder="Search name or staff code"
          id="sm-product-search"
          onChange={handleSearchChange}
          onFocus={() => {
            setKeyboardType({
              searchCus: false,
              parkedBill: false,
              salesHistory: false,
              salesHistoryCus: false,
              product: false,
              giftCardSearch: false,
              salesRep: true,
              addCustomer: false,
            });
            setTimeout(() => {
              keyboardRef?.current?.setInput(salesRepValue);
              setOpen(true);
            }, 300);
          }}
          onPressEnter={(e) => e.preventDefault()}
          prefix={<SearchOutlined />}
        />
        <div className="v4m-list" role="listbox" aria-label="Sales reps">
          {canClear && (
            <button
              type="button"
              role="option"
              aria-selected={!currentRepId}
              className={`v4m-row${!currentRepId ? " on" : ""}`}
              onClick={() => assignSalesRep({ s_salesrep_id: null, name: null })}
            >
              <span className="v4m-av none">—</span>
              <span className="v4m-name">No sales rep</span>
              {!currentRepId && <span className="v4m-check">Current</span>}
            </button>
          )}
          {salesRepData?.map((res) => {
            const on = !!currentRepId && res.s_salesrep_id === currentRepId;
            const busy = selectedSalesRep.code === res.code;
            return (
              <button
                type="button"
                role="option"
                aria-selected={on}
                key={res.code}
                disabled={busy}
                className={`v4m-row${on || busy ? " on" : ""}`}
                onClick={() => {
                  setSelectedSalesRep(res);
                  assignSalesRep(res);
                }}
              >
                <span className="v4m-av">{initialsOf(res.name)}</span>
                <span className="v4m-name">
                  {res.name}
                  {res.code ? <span className="v4m-code">{res.code}</span> : null}
                </span>
                {on && <span className="v4m-check">Current</span>}
              </button>
            );
          })}
          {!salesRepData?.length && salesRepValue ? <div className="v4m-empty">No sales rep matches “{salesRepValue}”.</div> : null}
        </div>
      </Modal>

      {open && posConfig?.showKeyboard === "Y" && (
        <Drawer placement="bottom" closable={false} styles={{ body: { paddingBottom: 0 } }} height="47vh" open={open && posConfig?.showKeyboard === "Y"} mask={false}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1vh" }}>
            <img src={Close} onClick={() => setOpen(false)} style={{ cursor: "pointer", width: "1.5vw" }} />
          </div>
          <div style={{ padding: "0 4vw 0" }}>
            <Keyboard
              keyboardRef={(r) => { keyboardRef.current = r; }}
              onChange={(val) => {
                if (keyboardType.salesRep) {
                  setSalesRepValue(val);
                  handleSearchChange({ target: { value: val } });
                  if (salesRepRef && salesRepRef.current) {
                    setTimeout(() => { salesRepRef.current?.focus(); }, 10);
                  }
                }
              }}
              layoutName={layoutName}
              display={{
                "{bksp}": "⌫",
                "{numbers}": "123",
                "{shift}": "⇧",
                "{enter}": "Enter",
                "{space}": "Space",
                "{number}": "123",
                "{caps}": "ABC",
                "{small}": "abc",
              }}
              layout={{
                shift: ["q w e r t y u i o p", "a s d f g h j k l", "{shift} z x c v b n m {bksp}", "{number} @ {space} . {enter}"],
                numbers: ["1 2 3 4 5 6 7 8 9 0", '- / : ; ( ) $ & @ "', "# . , _ ! ` {bksp}", "{caps} {space} {enter}"],
                number: ["1 2 3 4 5 6 7 8 9 0", '- / : ; ( ) $ & @ "', "# . , _ ! ` {bksp}", "{small} {space} {enter}"],
                default: ["Q W E R T Y U I O P", "A S D F G H J K L", "{shift} Z X C V B N M {bksp}", "{numbers} @ {space} . {enter}"],
              }}
              onKeyPress={handleKeyboardInput}
            />
          </div>
        </Drawer>
      )}
    </>
  );
};

export default SalesRepDrawer;
